/**
 * SubjectDocUploader — subida de N documentos atados a un sujeto
 * (representative / apoderado / beneficial_owner / company).
 *
 * Reutiliza el endpoint ``POST /api/customer-drafts/{draftId}/upload-document``
 * con ``subject_type`` y ``subject_draft_id`` para que el backend ate el
 * doc al sujeto correcto y el OCR caiga en su bucket.
 *
 * Multi-archivo: si ``multi`` está activo, no reemplaza el upload anterior —
 * se agregan a la lista. Para los doc_types acta/asambleas/poder esto se
 * activa automáticamente.
 */
import React, { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Upload, Check, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MULTI_DOCS = new Set(["acta_constitutiva", "asambleas", "poder"]);

export default function SubjectDocUploader({
  draftId,
  subjectType,
  subjectDraftId,
  docs = [],
  onUploaded,
}) {
  const [statuses, setStatuses] = useState({});  // docType → { uploading, error, success }
  const [uploadedCount, setUploadedCount] = useState({});

  async function upload(docType, file) {
    setStatuses((s) => ({ ...s, [docType]: { uploading: true } }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      fd.append("subject_type", subjectType);
      if (subjectDraftId) fd.append("subject_draft_id", subjectDraftId);
      await axios.post(
        `${API}/api/customer-drafts/${draftId}/upload-document`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
      );
      setStatuses((s) => ({ ...s, [docType]: { success: true } }));
      setUploadedCount((c) => ({ ...c, [docType]: (c[docType] || 0) + 1 }));
      if (typeof onUploaded === "function") {
        onUploaded({ docType, subjectType, subjectDraftId });
      }
    } catch (e) {
      setStatuses((s) => ({
        ...s,
        [docType]: { error: e.response?.data?.detail || "Error al subir" },
      }));
    }
  }

  return (
    <div className="space-y-3">
      {docs.map(({ docType, label, required }) => {
        const isMulti = MULTI_DOCS.has(docType);
        const st = statuses[docType] || {};
        const count = uploadedCount[docType] || 0;
        const inputId = `up-${subjectDraftId || "co"}-${docType}`;
        return (
          <div key={docType} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/40 p-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-100 truncate">
                {label} {required && <span className="text-rose-400">*</span>}
                {isMulti && count > 0 && (
                  <span className="ml-2 text-xs text-teal-300">({count} subido{count !== 1 ? "s" : ""})</span>
                )}
              </div>
              {st.error && <div className="text-xs text-rose-300 mt-1">{st.error}</div>}
              {st.success && !st.uploading && !isMulti && (
                <div className="text-xs text-teal-300 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Cargado
                </div>
              )}
            </div>
            <label htmlFor={inputId}>
              <input
                id={inputId}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                data-testid={`upload-${subjectDraftId || "co"}-${docType}`}
                onChange={(e) => e.target.files?.[0] && upload(docType, e.target.files[0])}
              />
              <Button
                asChild
                variant="outline"
                size="sm"
                disabled={st.uploading}
                className="cursor-pointer"
              >
                <span>
                  {st.uploading
                    ? "Subiendo…"
                    : (isMulti ? <>+ Agregar otro <Upload className="ml-1 w-3.5 h-3.5 inline" /></>
                              : <>Subir <Upload className="ml-1 w-3.5 h-3.5 inline" /></>)}
                </span>
              </Button>
            </label>
          </div>
        );
      })}
    </div>
  );
}
